import { ref, watch } from 'vue';
import { buildListingLink } from '../../utils/utils.js';
import * as L from 'leaflet/dist/leaflet-src.esm';
import 'leaflet/dist/leaflet.css';
import { divIcon } from 'leaflet';

/*
    Code which handles MAP functionality
*/
export function useMap(
    config,
    regionsStore,
    locationsStore,
    resultsStore,
    queryParams,
    mapStore,
) {
    const markers = {};

    let bounds = L.latLngBounds();

    let map = null;

    let queryHasBounds = false;

    const mapId = config.map.mapId;

    /*
        toggle map visibility on / off
    */
    function toggleMap() {
        mapStore.toggleShowMap();
    }

    /*
        initialise the map!

        1. if:
            a) there is no mapId
            b)'showMap'is set to false, or
            c) the map has already been loaded,
            then do nothing!
        2. otherwise:
            a) grab tiles from mapbox,
            b) initialise the map using Leaflet's functionality
            c) use our renderListingMarkers() function to render
                any listing markers and reset the map bounds
            d) watch the results store, and update the listing
                markers every time we have new results
            e) allow visitors to search by map bounds, via a
                'search this area' button which becomes visible
                any time the map has either:
                    i) zoomed in or out
                    ii) scrolled more than 10% on either axis
    */
    function initMap() {
        if (!mapId || !mapStore.showMap) {
            return;
        }

        const mapElement = document.getElementById(mapId);

        if (mapElement.getAttribute('data-isloaded') === 'true') {
            return;
        }

        // Initialize the map without any layers
        map = L.map(mapId, config.map.options);
        map.scrollWheelZoom.disable();

        clearListingsMarkers();

        bounds = L.latLngBounds();

        // Render markers and calculate bounds
        renderListingsMarkers();

        if (bounds.isValid()) {
            map.fitBounds(bounds);
            // Set view with appropriate zoom level
            const center = bounds.getCenter();
            const zoom = map.getBoundsZoom(bounds);
            map.setView(center, zoom);
        }

        map.once('moveend', function() {
            // Add the tile layer after setting the view
            const mapboxTiles = L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=' + config.map.accessToken, {
                attribution: '© <a href="https://www.mapbox.com/feedback/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                tileSize: 512,
                zoomOffset: -1,
                id: 'mapbox/streets-v11',
            });

            mapboxTiles.addTo(map);
        });

        mapElement.setAttribute('data-isloaded', true);

        watch(resultsStore, () => {
            mapStore.setDisableMapMoveEvent(false);
            clearListingsMarkers();
            bounds = L.latLngBounds();
            renderListingsMarkers();
            if (bounds.isValid()) {
                map.fitBounds(bounds);
                const center = bounds.getCenter();
                const zoom = map.getBoundsZoom(bounds);
                map.setView(center, zoom);
            }
        });

        watch(regionsStore, () => {
            if (regionsStore.chosenRegion) {
                queryParams.updateParamString('bounds', null);
            }
        });

        watch(locationsStore, () => {
            if (locationsStore.selectedLocations.length) {
                queryParams.updateParamString('bounds', null);
            }
        });

        map.on('moveend', function (e) {
            const newBounds = map.getBounds();
            if (mapStore.disableMapMoveEvent) {
                mapStore.setDisableMapMoveEvent(false);
                return;
            }
            if (mapHasMovedEnough(bounds, newBounds, 0.1)) {
                mapStore.setDisableMapMoveEvent(true);
                mapStore.setShowSearchButton(true);
            }
        });

        map.on('zoomend', function () {
            if (mapStore.disableMapMoveEvent) {
                mapStore.setDisableMapMoveEvent(false);
                return;
            }
            mapStore.setDisableMapMoveEvent(true);
            mapStore.setShowSearchButton(true);
        });
    }

    /*
        update the 'bounds' query param using the latest
        map bounds value

        nb: this function first removes any 'chosen region'
        from the regions store, and with it any chosen
        locations
    */
    function searchByBounds() {
        regionsStore.setChosenRegion('');
        locationsStore.updateSelectedLocations([]);
        resultsStore.setLoading(true);
        mapStore.setShowSearchButton(false);

        const newBounds = map.getBounds();

        queryParams.updateParamString('bounds', {
            "northEast": {
                "latitude": newBounds._northEast.lat,
                "longitude": newBounds._northEast.lng
            },
            "southWest": {
                "latitude": newBounds._southWest.lat,
                "longitude": newBounds._southWest.lng
            }
        });

        queryHasBounds = true;
    }

    /*
        remove all listing markers from the map
    */
    function clearListingsMarkers() {
        for (let identifier in markers) {
            if (markers.hasOwnProperty(identifier)) {
                map.removeLayer(markers[identifier]);
            }
        }
    }

    /*
        render any listing markers and make sure that they
        are all visible by resetting the map bounds
    */
    function renderListingsMarkers() {
        if (!resultsStore.listings) {
            return;
        }

        for (let listing of resultsStore.listings) {
            if (listing.node.latitude && listing.node.longitude) {
                bounds.extend([listing.node.latitude, listing.node.longitude]);
            }
            addMarker(listing.node);
        }

        if (queryHasBounds) {
            bounds = map.getBounds();
            mapStore.setDisableMapMoveEvent(false);
        } else if (bounds.isValid()) {
            map.fitBounds(bounds);
        }
    }

    /*
        determine whether the map has moved more than 'coefficient'
        amount along either axis
    */
    function mapHasMovedEnough(oldBounds, newBounds, coefficient) {
        /*
            latDist and lngDist give us the width and height of the map, in lat and lng.
            This number is multipled by our coefficient (e.g. 0.1 to return true if we
            have moved 10% in any direction)
        */
        const latDist = Math.abs((oldBounds._northEast.lat - oldBounds._southWest.lat) * coefficient);
        const lngDist = Math.abs((oldBounds._southWest.lng - oldBounds._northEast.lng) * coefficient);

        // latDiff and lngDiff give us the amount we've moved, in lat and lng
        const latDiff = Math.abs(((oldBounds._northEast.lat - newBounds._northEast.lat) + (oldBounds._southWest.lat - newBounds._southWest.lat)) / 2);
        const lngDiff = Math.abs(((oldBounds._northEast.lng - newBounds._northEast.lng) + (oldBounds._southWest.lng - newBounds._southWest.lng)) / 2);

        // check whether we've moved far enough in either direction!
        return latDiff > latDist || lngDiff > lngDist;
    }

    /*
        add a specific listing's marker to the map
    */
    function addMarker(listing) {
        let lat = parseFloat(listing.latitude),
            lng = parseFloat(listing.longitude);

        if (!lat || !lng) {
            return;
        }

        let iconObject;
        const iconSvg = listing.mainCategory.mapIconSvg;
        if (iconSvg) {
            iconObject = L.divIcon({
                iconSize: [48, 48],
                iconAnchor: [18, 30],
                popupAnchor: [0, -30],
                className: 'listing-map-icon-wrapper',
                html: `<div>${iconSvg}</div>`,
            });
        } else {
            iconObject = L.icon({
                iconUrl: listing.mapIconPath,
                iconSize: [listing.mapIconWidth, listing.mapIconHeight],
                iconAnchor: [listing.anchorX ? listing.anchorX : 18, listing.anchorY ? listing.anchorY : 48],
                popupAnchor: [listing.popupAnchorX ? listing.popupAnchorX : 0, listing.popupAnchorY ? listing.popupAnchorY : -48],
            });
        }
        const marker = L.marker([lat, lng], {
            icon: iconObject,
        }).addTo(map);

        const image = listing.image && listing.image.urlForList ? listing.image.urlForList : '';
        const website = listing.website ? `| <a href=${listing.website} target="_blank">Visit Website</a>` : '';
        const summary = listing.limitedSummary ? `<p class="summary">${listing.limitedSummary}</p>` : '';
        const type = listing.type;
        const link = buildListingLink(listing.urlSlug);

        let content = '';

        if (image) {
            content = `
                <div id="listings-map-infowindow">
                    <div class="gmap-img"><a href="${link}" style="background-image: url('${image}')"> </a>
                    </div>
                    <div class="content">
                        <p>
                            <a href=${link}><strong>${listing.name}</strong></a>
                        </p>
            `;
        } else {
            content = `
                <div id="listings-map-infowindow">
                    <div class="content">
                        <p>
                            <a href=${link}><strong>${listing.name}</strong></a>
                        </p>
            `;
        }

        if (type == "Track") {
            content += `
                    <p>
                        <b>Distance: </b>${listing.distance}km
                        &nbsp;&nbsp;&nbsp;
                        <b>Grade: </b>${listing.trackGradeName}
                    </p>
                </div>
            `;
        }
        content += `
                    ${summary}
                    <p class="link">
                        <a href=${link}>More Info</a>
                        ${website}
                    </p>
                </div>
            </div>
        `;

        marker.bindPopup(content);
        marker.on('click', markerClicked);
        markers[listing.identifier] = marker;
    }

    function markerClicked() {
        mapStore.setDisableMapMoveEvent(true);
    }

    return {
        initMap,
        toggleMap,
        searchByBounds,
    }
}
